import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";

// Avatar tints cycle so a list of visitors doesn't come out one flat colour.
const variants = ["green", "amber", "indigo", "navy"];

const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

// The API sends a timestamp; the card reads better as "2h ago".
const relativeTime = (value) => {
  if (!value) return "";

  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return String(value);

  const seconds = Math.round((Date.now() - then.getTime()) / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  return `${Math.round(hours / 24)}d ago`;
};

const isResolved = (status) => String(status || "").toLowerCase() === "resolved";

const statusLabel = (status) => {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/[-_]/g, " ");
};

export default function RecentConversations({
  conversations = [],
  assistantId = "",
  loading = false,
}) {
  const navigate = useNavigate();

  // The deep link the conversations screen reads, so a row opens straight
  // onto that transcript rather than the default first one.
  const open = (id) => {
    const params = new URLSearchParams({ conversationId: id });
    if (assistantId) params.set("assistantId", assistantId);
    navigate(`${RoutesPaths.CONVERSATIONS}?${params.toString()}`);
  };

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="dash-card-title">Recent conversations</h2>
        <Link to={RoutesPaths.CONVERSATIONS} className="dash-card-link">
          View all
        </Link>
      </div>

      {loading ? (
        <ul className="dash-conversations">
          {[0, 1, 2].map((row) => (
            <li key={row}>
              <span className="skel dash-conversation-avatar" />
              <div className="dash-conversation-text">
                <span className="skel acct-skel--label" />
                <span className="skel acct-skel--desc" />
              </div>
            </li>
          ))}
        </ul>
      ) : conversations.length === 0 ? (
        <p className="dash-empty">No conversations in this period yet.</p>
      ) : (
        <ul className="dash-conversations">
          {conversations.map((item, index) => (
            <li key={item.id || `${item.name}-${index}`}>
              {/* A button, not a list item with a click handler: opening a
                  conversation has to be reachable from the keyboard. */}
              <button
                type="button"
                className="dash-conversation-hit"
                onClick={() => item.id && open(item.id)}
                disabled={!item.id}
              >
                <span
                  className={`dash-conversation-avatar dash-avatar--${variants[index % variants.length]}`}
                >
                  {initialsOf(item.name)}
                </span>
                <div className="dash-conversation-text">
                  <strong>{item.name}</strong>
                  <span>{item.detail}</span>
                </div>
                <div className="dash-conversation-meta">
                  <span className="dash-conversation-time">
                    {relativeTime(item.time)}
                  </span>
                  {item.status && (
                    <span
                      className={`dash-status-badge${isResolved(item.status) ? " is-resolved" : " is-progress"}`}
                    >
                      {statusLabel(item.status)}
                    </span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
